import { Button, Flex, NumberInput, Select, Switch } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconDeviceFloppy } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import LabelText from '../../../../components/LabelText/LabelText';
import type { ModuleToEditComponentProps } from '../../../../components/ModuleToEditModal';
import { useI18n } from '../../../../contexts/I18nProvider';
import VariablePayrollService from '../../../../services/VariablePayrollService';
import type {
  TimeRecorderParams as TimeRecorderParamsType,
  VariablePayroll,
} from '../../../../types/api/response/module';

export default function TimeRecorderParams({
  module,
  updateModuleParams,
}: ModuleToEditComponentProps) {
  const { t } = useI18n();
  const moduleParams = module.configuration?.params as TimeRecorderParamsType;

  const { data: variablesPayrollData } = useQuery({
    queryKey: [
      'VariableParollService.getVariables',
      module.configuration.clientId,
    ],
    queryFn: () =>
      VariablePayrollService.getVariables(module.configuration.clientId),
  });
  const variablesPayroll: VariablePayroll[] =
    variablesPayrollData?.variablesPayroll || [];

  const form = useForm<TimeRecorderParamsType>({
    initialValues: {
      linkToDailyWork: moduleParams?.linkToDailyWork || false,
      overtimeInCounter: moduleParams?.overtimeInCounter || false,
      delayPeriod: moduleParams?.delayPeriod || 0,
      physicalDeviceSerialNumber:
        moduleParams?.physicalDeviceSerialNumber || '',
      endMonthVariableId: moduleParams?.endMonthVariableId || '',
    },
  });

  function submit(values: TimeRecorderParamsType) {
    updateModuleParams({
      apiUrl: module.description.url,
      payload: {
        active: true,
        ...values,
      },
    });
  }

  return (
    <form onSubmit={form.onSubmit(submit)}>
      <Flex direction={'column'} gap={'xl'}>
        <Switch
          labelPosition="left"
          label={t('w.linkToTimeManagementFunctionality')}
          checked={form.values.linkToDailyWork}
          {...form.getInputProps('linkToDailyWork')}
        />
        <Switch
          labelPosition="left"
          label={t('w.putOvertimeInCounter')}
          checked={form.values.overtimeInCounter}
          {...form.getInputProps('overtimeInCounter')}
        />
        <NumberInput
          label={<LabelText text={t('w.tolerancePeriodInMinutes')} />}
          description={t('w.defineTolerancePeriodForTimeClock')}
          {...form.getInputProps('delayPeriod')}
        />
        {Boolean(form.values.physicalDeviceSerialNumber) && (
          <>
            <Select
              data={variablesPayroll.map((variable) => {
                return {
                  value: variable.id ? variable.id : '',
                  label: variable.label,
                };
              })}
              label={<LabelText text={t('w.timeRecorderEndMonthVariable')} />}
              disabled={variablesPayroll.length === 0}
              onChange={(value) =>
                form.setFieldValue('endMonthVariableId', value)
              }
              clearable
              value={form.values.endMonthVariableId || ''}
              onClear={() => form.setFieldValue('endMonthVariableId', null)}
            />
            <LabelText
              text={`${t('w.terminalSerialNumber')} : ${
                form.values.physicalDeviceSerialNumber
              }`}
            />
          </>
        )}
        <Flex justify={'flex-end'} mt={'xl'}>
          <Button type={'submit'} leftSection={<IconDeviceFloppy />}>
            {t('w.saveAndActivate')}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
}
